import { useCallback, useEffect, useState, useMemo } from 'react';
import { getRegionsListAction } from 'features/regions/redux/actions';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import type { AppTableColumn, AppTableState } from 'shared/UI/UIKit/Table';
import type { Region, RegionsListRequest } from 'services/api';
import { clearState, setSelectedRegion } from '../../redux/slice';
import { getRegionsTableColumns } from './columns';

const getInitialStateWithSorters = (columns: AppTableColumn<Region>[]): AppTableState => {
  return {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => key !== 'controls')
      .map(({ key, dataIndex }) => {
        return key === 'name'
          ? {
              key,
              dataIndex,
              order: 'ascend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: getDefaultTablePagination(),
  };
};

export const useRegions = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const dispatch = useAppDispatch();
  const { regions, total, loading } = useAppSelector((state) => state.regionsState);
  const [siderVisible, setSiderVisible] = useState(false);

  const editRegion = useCallback(
    (id: number) => {
      const selectedRegion = regions.find((region) => region.id === id);

      if (selectedRegion) {
        dispatch(setSelectedRegion(selectedRegion));
        setEditModalVisible(true);
      }
    },
    [regions, dispatch],
  );

  const removeRegion = useCallback(
    (id: number) => {
      const selectedRegion = regions.find((region) => region.id === id);

      if (selectedRegion) {
        dispatch(setSelectedRegion(selectedRegion));
        setRemoveModalVisible(true);
      }
    },
    [regions, dispatch],
  );

  const columns = useMemo(
    () => getRegionsTableColumns(editRegion, removeRegion),
    [editRegion, removeRegion],
  );
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } =
    useFilteredColumns(columns);

  const getTableItems = useCallback(
    (nextTableState: AppTableState, nextFilterState: Record<string, any>) => {
      const {
        pagination: { current, pageSize },
        sorters,
      } = nextTableState;
      const sort = sorters
        .filter((col) => !!col.order)
        .map((col) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

      // Адаптируем параметры для API регионов (page/size вместо page/limit)
      const apiParams: RegionsListRequest = {
        page: current! - 1,
        size: pageSize,
        sort: sort.length > 0 ? sort : undefined,
        ...nextFilterState,
      };

      dispatch(getRegionsListAction(apiParams));
    },
    [dispatch],
  );

  const initialState = useMemo(() => getInitialStateWithSorters(columns), [columns]);

  const { tableState, onTableChange, filterState, filterCount, onFilterChange, onItemRemove } =
    useTableSearchParams(initialState, getTableItems, regions.length, total, hiddenColumns);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const createRegion = useCallback(() => {
    setCreateModalVisible(true);
  }, []);

  const showSider = useCallback(() => {
    setSiderVisible(true);
  }, []);

  const hideSider = useCallback(() => {
    setSiderVisible(false);
  }, []);

  return {
    state: {
      loading,
      regions,
      removeModalVisible,
      createModalVisible,
      editModalVisible,
      tableState,
      siderVisible,
      columns,
      filteredColumns,
      visibleKeys,
      filterState,
      filterCount,
    },
    methods: {
      setRemoveModalVisible,
      createRegion,
      editRegion,
      removeRegion,
      setCreateModalVisible,
      setEditModalVisible,
      onTableChange,
      showSider,
      hideSider,
      setVisibleKeys,
      onFilterChange,
      onItemRemove,
    },
  };
};
